import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DOMPurify from "isomorphic-dompurify";
import { supabase } from "@/integrations/supabase/client";

interface CvPdfOptions {
	htmlContent: string;
	filename: string;
	styles?: string;
}

/**
 * Convert each <img> src to a base64 data URI so html2canvas can render it
 * without CORS/tainted-canvas issues. Tries fetch first (works with any
 * CORS-enabled origin like Supabase public buckets); falls back to a
 * crossOrigin Image + canvas. If both fail, the image is removed so it
 * doesn't taint the final canvas.
 */
async function inlineImageViaFetch(src: string): Promise<string> {
	const response = await fetch(src, { mode: "cors", cache: "no-cache" });
	if (!response.ok) throw new Error(`fetch ${response.status}`);
	const blob = await response.blob();
	return await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(new Error("read failed"));
		reader.readAsDataURL(blob);
	});
}

async function inlineImageViaCanvas(src: string): Promise<string> {
	return await new Promise<string>((resolve, reject) => {
		const tempImg = new Image();
		tempImg.crossOrigin = "anonymous";
		const timer = setTimeout(() => reject(new Error("timeout")), 10000);
		tempImg.onload = () => {
			clearTimeout(timer);
			try {
				const canvas = document.createElement("canvas");
				canvas.width = tempImg.naturalWidth;
				canvas.height = tempImg.naturalHeight;
				canvas.getContext("2d")?.drawImage(tempImg, 0, 0);
				resolve(canvas.toDataURL("image/png"));
			} catch {
				reject(new Error("tainted"));
			}
		};
		tempImg.onerror = () => {
			clearTimeout(timer);
			reject(new Error("load failed"));
		};
		// Cache-bust to force a fresh CORS request rather than reusing a
		// cached non-CORS response.
		const sep = src.includes("?") ? "&" : "?";
		tempImg.src = `${src}${sep}_cv=${Date.now()}`;
	});
}

async function inlineImageViaProxy(src: string): Promise<string> {
	const { data, error } = await supabase.functions.invoke("proxy-image", {
		body: { url: src },
	});
	if (error) throw error;
	if (!data?.dataUri) throw new Error("proxy: no dataUri");
	return data.dataUri as string;
}

async function inlineImages(container: HTMLElement): Promise<void> {
	const images = container.querySelectorAll("img");
	const promises = Array.from(images).map(async (img) => {
		const src = img.getAttribute("src");
		if (!src || src.startsWith("data:")) return;

		let dataUri: string | null = null;
		try {
			dataUri = await inlineImageViaFetch(src);
		} catch {
			try {
				dataUri = await inlineImageViaCanvas(src);
			} catch {
				try {
					dataUri = await inlineImageViaProxy(src);
				} catch {
					dataUri = null;
				}
			}
		}

		if (dataUri)
			img.src = dataUri;
		else
			img.remove();
	});
	await Promise.all(promises);
}

/**
 * Find Y positions (in DOM pixels, relative to container top) where a page
 * break is safe — i.e. between CV sections or between experience items.
 */
function findBreakPoints(container: HTMLElement): number[] {
	const containerTop = container.getBoundingClientRect().top;
	const points: Set<number> = new Set();

	// Top-level sections (.cv-section)
	const sections = container.querySelectorAll(".cv-section");
	sections.forEach((section) => {
		const rect = section.getBoundingClientRect();
		// Break point = top of each section (between sections)
		points.add(Math.round(rect.top - containerTop));
	});

	// Individual experience/skill items within sections (direct children divs with margin-bottom)
	const sections2 = container.querySelectorAll(".cv-section--experience, .cv-section--skills");
	sections2.forEach((section) => {
		const children = section.children;
		for (let i = 0; i < children.length; i++) {
			const child = children[i] as HTMLElement;
			if (child.tagName === "DIV" || child.tagName === "UL") {
				const rect = child.getBoundingClientRect();
				points.add(Math.round(rect.top - containerTop));
			}
		}
	});

	return Array.from(points).sort((a, b) => a - b);
}

/**
 * Given a max page height (in DOM px) and a list of safe break Y positions,
 * find the best break point that doesn't exceed maxHeight from the current offset.
 */
function findBestBreak(breakPoints: number[], offset: number, maxHeight: number): number {
	const pageEnd = offset + maxHeight;

	// Find the last break point that fits within this page
	let best = pageEnd; // fallback: hard cut
	for (let i = breakPoints.length - 1; i >= 0; i--) {
		if (breakPoints[i] <= pageEnd && breakPoints[i] > offset) {
			best = breakPoints[i];
			break;
		}
	}

	// If the best break is too close to the offset (less than 30% of page),
	// use the hard cut instead to avoid near-empty pages
	if (best - offset < maxHeight * 0.3)
		return pageEnd;

	return best;
}

export async function generateCvPdf({ htmlContent, filename, styles }: CvPdfOptions): Promise<Blob> {
	const container = document.createElement("div");
	container.style.position = "absolute";
	container.style.left = "-9999px";
	container.style.top = "0";
	container.style.width = "794px"; // A4 width at 96dpi
	container.style.backgroundColor = "#ffffff";
	container.style.fontFamily = "Arial, sans-serif";
	container.style.fontSize = "14px";
	container.style.lineHeight = "1.6";
	container.style.color = "#333333";
	container.style.padding = "40px";
	container.style.boxSizing = "border-box";

	if (styles) {
		const styleEl = document.createElement("style");
		styleEl.textContent = styles;
		container.appendChild(styleEl);
	}

	// Sanitize as defence in depth: section renderers escape candidate fields, but custom/template HTML is not guaranteed
	container.insertAdjacentHTML("beforeend", DOMPurify.sanitize(htmlContent));
	document.body.appendChild(container);

	try {
		await inlineImages(container);

		// Find safe page break positions BEFORE rendering to canvas
		const breakPoints = findBreakPoints(container);

		const canvasScale = 1.5;
		const canvas = await html2canvas(container, {
			scale: canvasScale,
			backgroundColor: "#ffffff",
			useCORS: true,
			logging: false,
			windowWidth: 794,
		});

		const pdf = new jsPDF({
			orientation: "portrait",
			unit: "pt",
			format: "a4",
		});

		// A4 = 595.28 x 841.89 pt
		const marginX = 30;
		const marginY = 30;
		const pdfContentWidth = 595.28 - marginX * 2;
		const pdfContentHeight = 841.89 - marginY * 2;

		const scaleFactor = pdfContentWidth / canvas.width;
		// Max page height in DOM pixels (before canvas scale)
		const maxPageDomHeight = pdfContentHeight / scaleFactor / canvasScale;

		let domOffset = 0; // current Y offset in DOM pixels
		const totalDomHeight = canvas.height / canvasScale;
		let pageNum = 0;

		while (domOffset < totalDomHeight) {
			const remaining = totalDomHeight - domOffset;

			let sliceDomHeight: number;
			if (remaining <= maxPageDomHeight) {
				// Last page — take everything remaining
				sliceDomHeight = remaining;
			} else {
				// Find a clean break point
				const breakY = findBestBreak(breakPoints, domOffset, maxPageDomHeight);
				sliceDomHeight = breakY - domOffset;
			}

			// Convert to canvas pixels
			const canvasY = Math.round(domOffset * canvasScale);
			const canvasSliceHeight = Math.round(sliceDomHeight * canvasScale);

			const pageCanvas = document.createElement("canvas");
			pageCanvas.width = canvas.width;
			pageCanvas.height = canvasSliceHeight;

			const ctx = pageCanvas.getContext("2d");
			if (ctx) {
				ctx.drawImage(
					canvas,
					0, canvasY, canvas.width, canvasSliceHeight,
					0, 0, canvas.width, canvasSliceHeight
				);
			}

			if (pageNum > 0) pdf.addPage();

			const pdfSliceHeight = canvasSliceHeight * scaleFactor;
			pdf.addImage(
				pageCanvas, "JPEG",
				marginX, marginY,
				pdfContentWidth, pdfSliceHeight,
				undefined,
				"MEDIUM"
			);

			// Page number footer
			pdf.setFontSize(8);
			pdf.setTextColor(150);
			pdf.text(`${pageNum + 1}`, 595.28 / 2, 841.89 - 15, { align: "center" });

			domOffset += sliceDomHeight;
			pageNum++;
		}

		return pdf.output("blob");
	} finally {
		document.body.removeChild(container);
	}
}
